import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api, { extractError } from "@/lib/api";
import { Lightning, Eye, EyeSlash, ArrowLeft } from "@phosphor-icons/react";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const { refresh: refreshUser } = useAuth();
  const [step, setStep] = useState(1); // 1 = enter email, 2 = enter OTP + new pass
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const startCooldown = () => {
    setResendCooldown(60);
    const t = setInterval(() => {
      setResendCooldown((v) => {
        if (v <= 1) { clearInterval(t); return 0; }
        return v - 1;
      });
    }, 1000);
  };

  const sendCode = async (e) => {
    if (e) e.preventDefault();
    if (!email.trim()) return toast.error("Enter your email");
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      toast.success("If an account exists, a reset code has been emailed.");
      setStep(2);
      startCooldown();
    } catch (err) { toast.error(extractError(err)); }
    finally { setBusy(false); }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) return toast.error("Enter the 6-digit code");
    if (newPassword.length < 6) return toast.error("Password must be at least 6 characters");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", {
        email: email.trim().toLowerCase(),
        otp: otp.trim(),
        new_password: newPassword,
      });
      toast.success("Password updated. Welcome back!");
      await refreshUser();
      navigate("/library");
    } catch (err) { toast.error(extractError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="forgot-password-page" className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-12 hero-grid">
      <div className="w-full max-w-md">
        <div className="border border-border bg-card p-8 rounded-sm">
          <div className="flex items-center gap-2 mb-2">
            <Lightning size={20} weight="fill" className="text-primary" />
            <span className="text-xs font-mono uppercase tracking-[0.2em] text-primary">
              {step === 1 ? "Forgot password" : "Enter reset code"}
            </span>
          </div>
          <h1 className="font-display font-black text-3xl tracking-tighter mb-2">
            {step === 1 ? "Reset your password" : "Almost there."}
          </h1>
          <p className="text-sm text-muted-foreground mb-8">
            {step === 1
              ? "Enter the email tied to your account. We'll send you a 6-digit code."
              : `Sent a 6-digit code to ${email}. Enter it below along with a new password.`}
          </p>

          {step === 1 && (
            <form onSubmit={sendCode} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-xs font-mono uppercase tracking-widest">Email</Label>
                <Input
                  id="email" type="email" required autoComplete="email"
                  data-testid="fp-email-input"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  className="bg-black rounded-sm border-border focus-visible:ring-primary"
                />
              </div>
              <Button
                type="submit" disabled={busy}
                data-testid="fp-send-code-btn"
                className="w-full bg-primary text-black hover:bg-primary/90 font-bold rounded-sm"
              >
                {busy ? "Sending..." : "Send reset code"}
              </Button>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={submitReset} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="otp" className="text-xs font-mono uppercase tracking-widest">6-digit code</Label>
                <Input
                  id="otp" type="text" required inputMode="numeric" maxLength={6} pattern="[0-9]{6}"
                  data-testid="fp-otp-input"
                  value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="123456"
                  className="bg-black rounded-sm border-border focus-visible:ring-primary font-mono tracking-[0.5em] text-center text-lg"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-password" className="text-xs font-mono uppercase tracking-widest">New password</Label>
                <div className="relative">
                  <Input
                    id="new-password" type={showPassword ? "text" : "password"} required minLength={6}
                    data-testid="fp-new-password-input"
                    value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                    className="bg-black rounded-sm border-border focus-visible:ring-primary pr-10"
                  />
                  <button
                    type="button" onClick={() => setShowPassword((v) => !v)}
                    data-testid="fp-toggle-password"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-muted-foreground hover:text-primary transition-colors"
                  >
                    {showPassword ? <EyeSlash size={18} weight="bold" /> : <Eye size={18} weight="bold" />}
                  </button>
                </div>
              </div>
              <Button
                type="submit" disabled={busy}
                data-testid="fp-reset-btn"
                className="w-full bg-primary text-black hover:bg-primary/90 font-bold rounded-sm"
              >
                {busy ? "Resetting..." : "Reset password & sign in"}
              </Button>
              <div className="flex items-center justify-between text-xs font-mono">
                <button
                  type="button" onClick={() => setStep(1)}
                  data-testid="fp-change-email-btn"
                  className="text-muted-foreground hover:text-primary flex items-center gap-1"
                >
                  <ArrowLeft size={12} weight="bold" /> Change email
                </button>
                <button
                  type="button"
                  onClick={sendCode}
                  disabled={resendCooldown > 0 || busy}
                  data-testid="fp-resend-code-btn"
                  className="text-primary hover:underline disabled:text-muted-foreground disabled:hover:no-underline disabled:cursor-not-allowed"
                >
                  {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend code"}
                </button>
              </div>
            </form>
          )}

          <p className="text-sm text-muted-foreground mt-6 text-center">
            Remembered it?{" "}
            <Link to="/login" data-testid="fp-to-login" className="text-primary hover:underline">
              Back to sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
